import type { PageCategory, EntityKind } from '../core/schema';

/**
 * 페이지 경로에서 카테고리와 엔티티 정보를 추출하는 매핑 로직
 */

export interface EntityInfo {
    kind: EntityKind;
    ticker?: string;
    market?: string;
    strategy_id?: string;
    portfolio_id?: string;
    report_id?: string;
}

export interface PageInfo {
    category: PageCategory;
    subtype?: string;              // 🆕 페이지 소분류
    route_template: string;
    entity: EntityInfo;
    path_params: Record<string, string>;
    query_slim: Record<string, string>;
}

/**
 * 기본 페이지 매핑 규칙
 */
const DEFAULT_PAGE_RULES: Array<{
    pattern: RegExp;
    category: PageCategory;
    subtype?: string;  // 🆕 경로에서 결정되는 subtype
    template: string;
    entity?: (params: Record<string, string>) => EntityInfo;
}> = [
        // ==================== 홈 ====================
        {
            pattern: /^\/$/,
            category: 'home',
            template: '/',
        },

        // ==================== 종목 상세 (stock_detail) ====================
        // 투자매력도 진단 - 순서 중요! 더 구체적인 패턴을 먼저
        {
            pattern: /^\/detail\/([^\/]+)\/(fin|sharpe|mmt)$/,
            category: 'stock_detail',
            subtype: 'diagnosis',  // diagnosis_fin, diagnosis_sharpe, diagnosis_mmt
            template: '/detail/[ticker]/[diagnosisType]',
            entity: (params) => ({
                kind: 'stock',
                ticker: params.ticker,
            }),
        },

        // 주문하기
        {
            pattern: /^\/detail\/([^\/]+)\/buy$/,
            category: 'stock_detail',
            subtype: 'buy',
            template: '/detail/[ticker]/buy',
            entity: (params) => ({
                kind: 'stock',
                ticker: params.ticker,
            }),
        },

        // 판매하기
        {
            pattern: /^\/detail\/([^\/]+)\/sell$/,
            category: 'stock_detail',
            subtype: 'sell',
            template: '/detail/[ticker]/sell',
            entity: (params) => ({
                kind: 'stock',
                ticker: params.ticker,
            }),
        },

        // 기본 종목 상세
        {
            pattern: /^\/detail\/([^\/]+)$/,
            category: 'stock_detail',
            subtype: 'default',
            template: '/detail/[ticker]',
            entity: (params) => ({
                kind: 'stock',
                ticker: params.ticker,
            }),
        },

        // ==================== 전략 상세 (strategy_detail) ====================
        {
            pattern: /^\/strategy\/([^\/]+)$/,
            category: 'strategy_detail',
            template: '/strategy/[id]',
            entity: (params) => ({
                kind: 'strategy',
                strategy_id: params.id,
            }),
        },

        // ==================== 리포트 상세 (report_detail) ====================
        {
            pattern: /^\/discuss$/,
            category: 'report_detail',
            template: '/discuss',
        },

        {
            pattern: /^\/discuss\/([^\/]+)$/,
            category: 'report_detail',
            template: '/discuss/[id]',
            entity: (params) => ({
                kind: 'report',
                report_id: params.id,
            }),
        },

        // ==================== 브리핑 (briefing) ====================
        {
            pattern: /^\/briefing$/,
            category: 'briefing',
            template: '/briefing',
        },

        // ==================== 랭킹 (ranking) ====================
        {
            pattern: /^\/scoreranking/,
            category: 'ranking',
            subtype: 'score',
            template: '/scoreranking',
        },

        {
            pattern: /^\/industryranking/,
            category: 'ranking',
            subtype: 'industry',
            template: '/industryranking',
        },

        {
            pattern: /^\/mmtranking/,
            category: 'ranking',
            subtype: 'mmt',
            template: '/mmtranking',
        },

        {
            pattern: /^\/sharperanking/,
            category: 'ranking',
            subtype: 'sharpe',
            template: '/sharperanking',
        },

        // ==================== 포트폴리오 (portfolio) ====================
        // 순서 중요! 더 구체적인 패턴을 먼저

        // 종목 선택
        {
            pattern: /^\/portfolio\/new\/add-assets/,
            category: 'portfolio',
            subtype: 'select_assets',
            template: '/portfolio/new/add-assets',
        },

        // 랜딩
        {
            pattern: /^\/portfolio\/new$/,
            category: 'portfolio',
            subtype: 'landing',
            template: '/portfolio/new',
        },

        // 티저
        {
            pattern: /^\/portfolio\/teaser\/([^\/]+)$/,
            category: 'portfolio',
            subtype: 'teaser',
            template: '/portfolio/teaser/[id]',
            entity: (params) => ({
                kind: 'portfolio',
                portfolio_id: params.id,
            }),
        },

        // 하이라이트
        {
            pattern: /^\/portfolio\/highlight\/([^\/]+)$/,
            category: 'portfolio',
            subtype: 'highlight',
            template: '/portfolio/highlight/[id]',
            entity: (params) => ({
                kind: 'portfolio',
                portfolio_id: params.id,
            }),
        },

        // 처방 전 설문
        {
            pattern: /^\/portfolio\/([^\/]+)\/recommand\/survey$/,
            category: 'portfolio',
            subtype: 'survey',
            template: '/portfolio/[id]/recommand/survey',
            entity: (params) => ({
                kind: 'portfolio',
                portfolio_id: params.id,
            }),
        },

        // 처방 결과
        {
            pattern: /^\/portfolio\/([^\/]+)\/recommand\/result$/,
            category: 'portfolio',
            subtype: 'diagnosis',
            template: '/portfolio/[id]/recommand/result',
            entity: (params) => ({
                kind: 'portfolio',
                portfolio_id: params.id,
            }),
        },

        // 종목 편집
        {
            pattern: /^\/portfolio\/([^\/]+)\/edit$/,
            category: 'portfolio',
            subtype: 'edit',
            template: '/portfolio/[id]/edit',
            entity: (params) => ({
                kind: 'portfolio',
                portfolio_id: params.id,
            }),
        },

        // 수량/평단 입력
        {
            pattern: /^\/portfolio\/([^\/]+)\/setting$/,
            category: 'portfolio',
            subtype: 'setting',
            template: '/portfolio/[id]/setting',
            entity: (params) => ({
                kind: 'portfolio',
                portfolio_id: params.id,
            }),
        },

        // 기본 포트폴리오
        {
            pattern: /^\/portfolio\/([^\/]+)$/,
            category: 'portfolio',
            template: '/portfolio/[id]',
            entity: (params) => ({
                kind: 'portfolio',
                portfolio_id: params.id,
            }),
        },

        {
            pattern: /^\/portfolio$/,
            category: 'portfolio',
            template: '/portfolio',
        },

        // ==================== 플래너 (planner) ====================
        // 설문 - consume/resume
        {
            pattern: /^\/saa\/(consume|resume)\/survey$/,
            category: 'planner',
            subtype: 'survey',
            template: '/saa/[category]/survey',
        },

        // 결과
        {
            pattern: /^\/saa\/(consume|resume)\/result$/,
            category: 'planner',
            subtype: 'result',
            template: '/saa/[category]/result',
        },

        // 카테고리 선택
        {
            pattern: /^\/saa\/category$/,
            category: 'planner',
            subtype: 'category',
            template: '/saa/category',
        },

        // 랜딩
        {
            pattern: /^\/saa\/new$/,
            category: 'planner',
            subtype: 'landing',
            template: '/saa/new',
        },

        // 기타 saa 경로
        {
            pattern: /^\/saa/,
            category: 'planner',
            template: '/saa',
        },

        // ==================== KB (kb) ====================
        // 거래내역
        {
            pattern: /^\/accountdetail\/transactions$/,
            category: 'kb',
            subtype: 'tr_history',
            template: '/accountdetail/transactions',
        },

        // 계좌 정보
        {
            pattern: /^\/kb\/accountdetail$/,
            category: 'kb',
            subtype: 'my',
            template: '/kb/accountdetail',
        },

        // 주문내역
        {
            pattern: /^\/kb\/order\/list$/,
            category: 'kb',
            subtype: 'order_history',
            template: '/kb/order/list',
        },

        // 연결 확인
        {
            pattern: /^\/account\/check$/,
            category: 'kb',
            subtype: 'check',
            template: '/account/check',
        },

        // 약관동의
        {
            pattern: /^\/account\/terms$/,
            category: 'kb',
            subtype: 'terms',
            template: '/account/terms',
        },

        // 연결시작
        {
            pattern: /^\/account\/first$/,
            category: 'kb',
            subtype: 'first',
            template: '/account/first',
        },

        // KB 랜딩
        {
            pattern: /^\/kb\/welcome$/,
            category: 'kb',
            subtype: 'landing',
            template: '/kb/welcome',
        },

        // 기타 KB 경로
        {
            pattern: /^\/kb/,
            category: 'kb',
            template: '/kb',
        },

        // ==================== 설정 (settings) ====================
        // 알림설정
        {
            pattern: /^\/service\/alarm$/,
            category: 'settings',
            subtype: 'alarm',
            template: '/service/alarm',
        },

        // 내정보
        {
            pattern: /^\/service$/,
            category: 'settings',
            subtype: 'my',
            template: '/service',
        },

        // 기타 service 경로
        {
            pattern: /^\/service/,
            category: 'settings',
            template: '/service',
        },

        // ==================== 검색 (search) ====================
        {
            pattern: /^\/search/,
            category: 'search',
            template: '/search',
        },

        // ==================== 관심 종목 (collection) ====================
        {
            pattern: /^\/watchlist/,
            category: 'collection',
            template: '/watchlist',
        },

        {
            pattern: /^\/interest/,
            category: 'collection',
            template: '/interest',
        },

        // ==================== 투자성향검사 (tolerance) ====================
        {
            pattern: /^\/tolerance/,
            category: 'tolerance',
            template: '/tolerance',
        },

        // ==================== 인증 (auth) ====================
        {
            pattern: /^\/login/,
            category: 'auth',
            template: '/login',
        },

        {
            pattern: /^\/signup/,
            category: 'auth',
            template: '/signup',
        },

        // ==================== 에러 (error) ====================
        {
            pattern: /^\/404$/,
            category: 'error',
            subtype: 'not_found',
            template: '/404',
        },

        {
            pattern: /^\/500$/,
            category: 'error',
            subtype: 'server_error',
            template: '/500',
        },

        // ==================== 기타 정적 페이지 (static) ====================
        // 나머지는 모두 static으로 처리 (이 규칙은 마지막에 위치)
    ];

/**
 * 경로에서 매개변수 추출
 */
function extractParams(path: string, pattern: RegExp): Record<string, string> {
    const match = path.match(pattern);
    if (!match) return {};

    const params: Record<string, string> = {};

    // 패턴에 따른 파라미터 추출 로직
    // 종목 상세
    if (pattern.source.includes('detail')) {
        params.ticker = match[1];
        // 진단 타입이 있으면 추가 (fin, sharpe, mmt)
        if (match[2]) {
            params.diagnosisType = match[2];
        }
    }
    // 전략
    else if (pattern.source.includes('strategy')) {
        params.id = match[1];
    }
    // 포트폴리오
    else if (pattern.source.includes('portfolio')) {
        if (match[1]) params.id = match[1];
    }
    // 리포트/토론
    else if (pattern.source.includes('discuss')) {
        if (match[1]) params.id = match[1];
    }
    // 플래너 (consume/resume)
    else if (pattern.source.includes('saa')) {
        if (match[1]) params.category = match[1];
    }

    return params;
}

/**
 * 쿼리 파라미터에서 중요한 것만 추출 (PII 제외)
 */
function extractSlimQuery(searchParams: URLSearchParams): Record<string, string> {
    const slim: Record<string, string> = {};

    // 허용된 쿼리 파라미터만 포함
    const allowedParams = [
        'tab',
        'type',       // 🔧 페이지 타입 (예: home의 my/discover)
        'sort',
        'filter',
        'page',
        'limit',
        'category',
        'market',
        'range',
        'view',
        'from',
        'ref',
        'loc',
        'query',
    ];

    allowedParams.forEach(param => {
        const value = searchParams.get(param);
        if (value && value.length < 100) { // 길이 제한
            slim[param] = value;
        }
    });

    return slim;
}

/**
 * 페이지 정보 분석
 */
export function analyzePage(pathname: string, search = ''): PageInfo {
    const searchParams = new URLSearchParams(search);

    // 매칭 규칙 찾기
    for (const rule of DEFAULT_PAGE_RULES) {
        if (rule.pattern.test(pathname)) {
            const pathParams = extractParams(pathname, rule.pattern);
            const entity = rule.entity ? rule.entity(pathParams) : { kind: 'none' as const };

            // 🆕 page_subtype 우선순위: 규칙에 정의된 subtype (URL 경로 기반)
            const subtype = rule.subtype || undefined;
            return {
                category: rule.category,
                subtype,
                route_template: rule.template,
                entity,
                path_params: pathParams,
                query_slim: extractSlimQuery(searchParams),
            };
        }
    }

    // 매칭되지 않는 경우 기본값
    return {
        category: 'static',
        subtype: undefined,
        route_template: pathname,
        entity: { kind: 'none' },
        path_params: {},
        query_slim: extractSlimQuery(searchParams),
    };
}

/**
 * 커스텀 매핑 규칙 추가 (확장성을 위해)
 */
let customRules: typeof DEFAULT_PAGE_RULES = [];

export function addPageRule(
    pattern: RegExp,
    category: PageCategory,
    template: string,
    entityExtractor?: (params: Record<string, string>) => EntityInfo
): void {
    customRules.push({
        pattern,
        category,
        template,
        entity: entityExtractor,
    });
}

/**
 * 커스텀 규칙을 포함한 페이지 분석
 */
export function analyzePageWithCustom(pathname: string, search = ''): PageInfo {
    const searchParams = new URLSearchParams(search);

    // 커스텀 규칙 먼저 확인
    for (const rule of customRules) {
        if (rule.pattern.test(pathname)) {
            const pathParams = extractParams(pathname, rule.pattern);
            const entity = rule.entity ? rule.entity(pathParams) : { kind: 'none' as const };

            // 🆕 page_subtype 우선순위: 규칙에 정의된 subtype (URL 경로 기반)
            const subtype = rule.subtype || undefined;

            return {
                category: rule.category,
                subtype,
                route_template: rule.template,
                entity,
                path_params: pathParams,
                query_slim: extractSlimQuery(searchParams),
            };
        }
    }

    // 기본 규칙으로 폴백
    return analyzePage(pathname, search);
}

/**
 * Next.js 라우터와 연동하여 페이지 정보 추출
 */
export function analyzeNextPage(router: any): PageInfo {
    const pathname = router.pathname || router.asPath?.split('?')[0] || '/';
    const search = typeof window !== 'undefined' ? window.location.search : '';

    return analyzePageWithCustom(pathname, search);
}

/**
 * 현재 페이지 정보 (브라우저 환경에서만)
 */
export function getCurrentPageInfo(): PageInfo | null {
    if (typeof window === 'undefined') {
        return null;
    }

    return analyzePageWithCustom(window.location.pathname, window.location.search);
}

/**
 * 시장 정보 추출 헬퍼 (종목 티커에서)
 */
export function extractMarketFromTicker(ticker: string): string {
    // 한국 종목: 6자리 숫자 (예: 005930, 035720) 또는 6자리 숫자+K/L (예: 005930K, 035720L)
    if (/^\d{6}[KL]?$/.test(ticker)) {
        return 'kr';
    }

    // 가상화폐: 끝에 '-USD' 붙음 (예: BTC-USD, ETH-USD)
    if (ticker.endsWith('-USD')) {
        return 'crypto';
    }

    // 그 외는 미국 종목 (예: AAPL, NVDA, GOOGL)
    return 'us';
}

/**
 * 엔티티 정보 보강 헬퍼
 */
export function enrichEntityInfo(entity: EntityInfo, additionalData?: any): EntityInfo {
    const enriched = { ...entity };

    // 종목의 경우 시장 정보 보강
    if (entity.kind === 'stock' && entity.ticker && !entity.market) {
        enriched.market = extractMarketFromTicker(entity.ticker);
    }

    // 추가 데이터가 있으면 병합
    if (additionalData) {
        Object.assign(enriched, additionalData);
    }

    return enriched;
}
